import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { NewsHero } from "@/components/news-hero";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { getNewsArticle, newsPath } from "@/content/news";
import { getAuthor } from "@/lib/authors";

import { notFoundStatus } from "@/lib/http-status";
import { ogImageMeta } from "@/lib/og";
import { NOINDEX_META } from "@/lib/publication";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/nieuws/$slug")({
  loader: async ({ params }) => {
    const article = getNewsArticle(params.slug);
    if (!article) {
      await notFoundStatus();
      throw notFound();
    }
    return { article };
  },
  head: ({ loaderData }) => {
    const article = loaderData?.article;
    if (!article) {
      return { meta: [{ title: "Niet gevonden | Casino Betalingen" }, ...NOINDEX_META] };
    }
    const url = `${SITE.url}${newsPath(article)}/`;
    const author = getAuthor(article.authorSlug);
    const reviewer = getAuthor(article.reviewerSlug);
    return {
      meta: [
        { title: `${article.title} | Casino Betalingen` },
        { name: "description", content: article.description },
        { property: "og:title", content: article.title },
        { property: "og:description", content: article.description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta(article.ogName ?? "default"),
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.description,
            inLanguage: "nl-NL",
            datePublished: article.publishedAt,
            dateModified: article.updatedAt,
            mainEntityOfPage: url,
            articleSection: article.category,
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Nieuws", item: `${SITE.url}/nieuws/` },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          }),
        },
      ],
    };
  },
  component: NewsArticlePage,
});

function NewsArticlePage() {
  const { article } = Route.useLoaderData();
  const priorityPaths = article.related.map((l) => l.path);


  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <nav aria-label="Kruimelpad" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-1.5">/</span>
        <Link to="/nieuws/" className="hover:underline">Nieuws</Link>
      </nav>

      <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
        <article className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">{article.category}</Badge>
            <time dateTime={article.publishedAt} className="text-xs text-muted-foreground">
              Gepubliceerd {article.publishedLabel}
            </time>
          </div>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">{article.description}</p>

          <NewsHero
            className="mt-6"
            image={article.heroImage}
            methods={article.heroMethods}
            alt={article.heroAlt ?? article.title}
            eager
          />

          <div className="mt-6">
            <AuthorByline
              authorSlug={article.authorSlug}
              reviewerSlug={article.reviewerSlug}
              updatedAt={new Date(article.updatedAt + "T00:00:00Z").toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}
              readingMinutes={article.readingMinutes}
            />
          </div>

          <div className="mt-8 lg:hidden">
            <ArticleToc blocks={article.blocks} />
          </div>

          <div className="mt-8">
            <ArticleBody blocks={article.blocks} currentPath={newsPath(article)} priorityPaths={priorityPaths} />
          </div>

          <section className="mt-10 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">Bronnen</h2>
            <ul className="mt-3 space-y-2">
              {article.sources.map((source) => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {source.label}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <p className="mt-6 text-xs leading-6 text-muted-foreground">
            {SITE.disclaimer}
          </p>
        </article>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-28 lg:self-start">
          <div className="hidden lg:block">
            <ArticleToc blocks={article.blocks} />
          </div>
          <Card className="card-surface p-5">
            <h2 className="text-sm font-semibold text-foreground">Verder vergelijken</h2>
            <ul className="mt-3 space-y-2">
              {article.related.map((item) => (
                <li key={item.path}>
                  <RelatedLink label={item.label} path={item.path} />
                </li>
              ))}
            </ul>
          </Card>
        </aside>
      </div>
    </main>
  );
}

function RelatedLink({ label, path }: { label: string; path: string }) {
  const className = "text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline";
  if (path.startsWith("/betaalmethoden/") && path !== "/betaalmethoden/") {
    const slug = path.replace("/betaalmethoden/", "").replace(/\/$/, "");
    return (
      <Link to="/betaalmethoden/$slug/" params={{ slug }} className={className}>
        {label}
      </Link>
    );
  }
  if (path.startsWith("/casinozoeker")) {
    return <Link to="/casinozoeker/" className={className}>{label}</Link>;
  }
  return <Link to="/betaalmethoden/" className={className}>{label}</Link>;
}
